// EnrollPasskeyPage — public /enroll-passkey?token=… landing.
//
// Spec: docs/specs/location-tasks.md §7.2.
//
// Flow:
//   1. POST /v1/passkey/enroll/start { token } → PublicKeyCredentialCreationOptions
//   2. browser navigator.credentials.create({ publicKey: opts })
//      → user goes through platform Touch ID / Face ID / Windows Hello prompt
//   3. POST /v1/passkey/enroll/finish { token, attestationResponse }
//      → backend verifies + INSERT officer_devices, NULLs token
//
// Bundle: @simplewebauthn/browser handles ArrayBuffer ↔ base64url plumbing.

import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { KeyRound, CheckCircle2, AlertTriangle, Loader2, Fingerprint } from "lucide-react";
import { startRegistration } from "@simplewebauthn/browser";
import { resolveApiBase } from "@nexus/frontend-shared/api-client";
import { isSupportedPasskeyHost, PASSKEY_HOST_UNSUPPORTED_MESSAGE } from "../../lib/passkeyRp.js";

// Host-aware API base (NEXUS group 7): the enrol page is served on nexus, so
// resolveApiBase() → api.nexus.sentry.red. No hardcoded location fallback.
const API_BASE = resolveApiBase();

type Phase = "init" | "validating" | "ready" | "registering" | "submitting" | "done" | "error";

/** PublicKeyCredentialCreationOptionsJSON shape — match startRegistration's
 *  expected `optionsJSON` field. v11's types are not directly exported,
 *  so we infer from the function parameter. */
type RegistrationOptions = Parameters<typeof startRegistration>[0]["optionsJSON"];

interface StartResp {
  ok?: true;
  options?: RegistrationOptions;
  error?: string;
  correlation_id?: string;
}

interface FinishResp {
  ok?: true;
  device_id?: string;
  credential_id_prefix?: string;
  error?: string;
  message?: string;
}

const ERROR_LABEL: Record<string, string> = {
  token_format_invalid: "註冊連結格式錯誤。",
  invalid_or_expired_token: "註冊連結已失效或過期。請聯絡管理員重新發送。",
  officer_inactive: "此巡邏員已停用，無法註冊裝置。",
  challenge_expired_or_missing: "註冊流程逾時，請重新整理頁面後再試一次。",
  credential_already_registered: "此裝置已綁定過。如需更換裝置，請聯絡管理員。",
  verification_failed: "裝置驗證失敗。請確認手機 / 電腦支援 Passkey 後重試。",
  kv_unavailable: "後端服務暫時無法處理，請稍後再試。",
};

export default function EnrollPasskeyPage(): React.ReactElement {
  const [params] = useSearchParams();
  const token = (params.get("token") ?? "").trim();

  const [phase, setPhase] = useState<Phase>("init");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [options, setOptions] = useState<RegistrationOptions | null>(null);
  const [credentialPrefix, setCredentialPrefix] = useState<string | null>(null);

  // Step 1 — validate + fetch options
  useEffect(() => {
    // Hard cutover guard — a passkey can only be registered on the nexus
    // origin. If this page was opened from a legacy (location) link, the
    // browser would reject the ceremony with a confusing RP mismatch; show a
    // clear "re-enrol on the new domain" message instead.
    if (typeof window !== "undefined" && !isSupportedPasskeyHost(window.location.hostname)) {
      setErrMsg(PASSKEY_HOST_UNSUPPORTED_MESSAGE);
      setPhase("error");
      return;
    }
    if (!token) {
      setErrMsg("缺少註冊連結 token。");
      setPhase("error");
      return;
    }
    if (!/^[0-9a-f]{64}$/i.test(token)) {
      setErrMsg("註冊連結格式錯誤。");
      setPhase("error");
      return;
    }
    setPhase("validating");
    fetch(`${API_BASE}/v1/passkey/enroll/start`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ token }),
    })
      .then(async (r) => {
        const j = await r.json().catch(() => ({})) as StartResp;
        if (!r.ok || !j.options) {
          setErrMsg(ERROR_LABEL[j.error ?? ""] ?? `驗證失敗（${j.error ?? r.status}）`);
          setPhase("error");
          return;
        }
        setOptions(j.options);
        setPhase("ready");
      })
      .catch(() => {
        setErrMsg("無法連線到伺服器，請檢查網路後再試。");
        setPhase("error");
      });
  }, [token]);

  const startCeremony = async () => {
    if (!options) return;
    setPhase("registering"); setErrMsg(null);
    let attestation;
    try {
      attestation = await startRegistration({ optionsJSON: options });
    } catch (e) {
      // Common errors: NotAllowedError (user canceled), InvalidStateError
      // (already registered), AbortError (timeout).
      const name = (e as Error).name ?? "Error";
      const msg = (e as Error).message ?? "";
      const friendly = name === "NotAllowedError"
        ? "您取消了驗證，或瀏覽器拒絕存取。請重試。"
        : name === "InvalidStateError"
        ? "此裝置已綁定過，無法重複註冊。"
        : `Passkey 註冊失敗：${name} — ${msg}`;
      setErrMsg(friendly);
      setPhase("error");
      return;
    }

    setPhase("submitting");
    try {
      const r = await fetch(`${API_BASE}/v1/passkey/enroll/finish`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token, attestationResponse: attestation }),
      });
      const j = await r.json().catch(() => ({})) as FinishResp;
      if (!r.ok || !j.ok) {
        setErrMsg(ERROR_LABEL[j.error ?? ""] ?? `提交失敗（${j.error ?? r.status}）${j.message ? "：" + j.message : ""}`);
        setPhase("error");
        return;
      }
      setCredentialPrefix(j.credential_id_prefix ?? null);
      setPhase("done");
    } catch (e) {
      setErrMsg("提交時連線中斷，請重試。" + ((e as Error).message ?? ""));
      setPhase("error");
    }
  };

  return (
    <div className="min-h-[100dvh] bg-bg-base text-text-primary flex flex-col items-center px-4 py-8">
      <header className="flex items-center gap-2 mb-5">
        <img
          src="/sentry-icon.svg"
          alt="SENTRY"
          className="h-8 w-auto"
          style={{ filter: "brightness(0) invert(1)" }}
        />
        <span className="text-sm font-semibold tracking-wide">NEXUS</span>
      </header>

      <main className="w-full max-w-md card bg-bg-surface border-border p-6 space-y-5 animate-fade-in">
        <div>
          <h1 className="text-xl font-semibold tracking-tight inline-flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-accent" />
            裝置綁定（Passkey）
          </h1>
          <p className="mt-2 text-xs text-text-secondary leading-relaxed">
            這個裝置將綁定到你的巡邏員帳號，未來執行需驗證裝置的任務時，
            會請你用 <span className="text-text-primary font-medium">Touch ID / Face ID / Windows Hello</span>
            等本機驗證一次。Passkey 不可匯出，丟手機即失效。
          </p>
        </div>

        {phase === "init" || phase === "validating" ? (
          <CenteredSpinner label="驗證連結中…" />
        ) : phase === "ready" ? (
          <button className="btn-primary w-full inline-flex items-center justify-center gap-2" onClick={() => void startCeremony()}>
            <Fingerprint className="h-4 w-4" />
            使用此裝置註冊 Passkey
          </button>
        ) : phase === "registering" ? (
          <CenteredSpinner label="正在等待您完成驗證…（Touch ID / Face ID / Windows Hello）" />
        ) : phase === "submitting" ? (
          <CenteredSpinner label="提交綁定中…" />
        ) : phase === "done" ? (
          <DonePane credPrefix={credentialPrefix} />
        ) : (
          <ErrorPane message={errMsg ?? "發生未知錯誤"} onRetry={() => { setErrMsg(null); setPhase("init"); }} />
        )}

        <p className="text-2xs text-text-muted text-center">
          Passkey 私鑰永遠存在裝置內、不會送到伺服器。系統只儲存可驗證的公鑰。
        </p>
      </main>
    </div>
  );
}

function CenteredSpinner({ label }: { label: string }): React.ReactElement {
  return (
    <div className="py-8 flex flex-col items-center gap-3 text-text-muted">
      <Loader2 className="h-6 w-6 animate-spin text-accent" />
      <p className="text-xs text-center">{label}</p>
    </div>
  );
}

function DonePane({ credPrefix }: { credPrefix: string | null }): React.ReactElement {
  return (
    <div className="space-y-3">
      <div className="rounded-md border border-success/40 bg-success/10 p-4 text-sm text-success flex items-start gap-2">
        <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0" />
        <div>
          <p className="font-medium">裝置綁定完成</p>
          <p className="text-xs mt-1 opacity-90">
            這個裝置現在可以驗證您的身分。如果你之後換手機，請聯絡管理員重新發送註冊連結。
          </p>
          {credPrefix && (
            <p className="text-2xs mt-1 opacity-80 font-mono">credential: {credPrefix}</p>
          )}
        </div>
      </div>
      <p className="text-2xs text-text-muted text-center">您現在可以關閉此頁面。</p>
    </div>
  );
}

function ErrorPane({ message, onRetry }: { message: string; onRetry: () => void }): React.ReactElement {
  return (
    <div className="space-y-3">
      <div className="rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <span>{message}</span>
      </div>
      <button className="btn-secondary w-full" onClick={onRetry}>重試</button>
    </div>
  );
}
